'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from '@/hooks/useTranslations'
import { Button } from './ui/button'
import { Paintbrush } from 'lucide-react'

export type BackgroundMode = 'paper' | 'gradient' | 'both' | 'none'

export function BackgroundSwitcher() {
  const t = useTranslations('backgroundSwitcher')
  const [mode, setMode] = useState<BackgroundMode>('paper')
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const saved = localStorage.getItem('imki-bg-mode') as BackgroundMode
    if (saved) setMode(saved)
  }, [])

  const changeMode = (newMode: BackgroundMode) => {
    setMode(newMode)
    localStorage.setItem('imki-bg-mode', newMode)
    window.dispatchEvent(new CustomEvent('background-mode-change', { detail: newMode }))
    setIsOpen(false)
  }

  return (
    <div className="fixed bottom-4 left-4 z-50">
      {isOpen && (
        <div className="mb-2 bg-white rounded-lg shadow-lg border p-2 space-y-1">
          <button
            onClick={() => changeMode('paper')}
            className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
              mode === 'paper' ? 'bg-blue-50 text-blue-700 font-medium' : ''
            }`}
          >
            {t('paperOnly')}
          </button>
          <button
            onClick={() => changeMode('gradient')}
            className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
              mode === 'gradient' ? 'bg-blue-50 text-blue-700 font-medium' : ''
            }`}
          >
            {t('gradientOnly')}
          </button>
          <button
            onClick={() => changeMode('both')}
            className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
              mode === 'both' ? 'bg-blue-50 text-blue-700 font-medium' : ''
            }`}
          >
            {t('bothCombined')}
          </button>
          <button
            onClick={() => changeMode('none')}
            className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
              mode === 'none' ? 'bg-blue-50 text-blue-700 font-medium' : ''
            }`}
          >
            {t('noneClean')}
          </button>
        </div>
      )}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        size="icon"
        variant="outline"
        className="bg-white shadow-lg"
        title={t('changeBackground')}
      >
        <Paintbrush className="w-4 h-4" />
      </Button>
    </div>
  )
}

// Inline background switcher for dropdown menus
export function BackgroundSwitcherInline() {
  const t = useTranslations('backgroundSwitcher')
  const [mode, setMode] = useState<BackgroundMode>('paper')

  useEffect(() => {
    const saved = localStorage.getItem('imki-bg-mode') as BackgroundMode
    if (saved) setMode(saved)
  }, [])

  const changeMode = (newMode: BackgroundMode) => {
    setMode(newMode)
    localStorage.setItem('imki-bg-mode', newMode)
    window.dispatchEvent(new CustomEvent('background-mode-change', { detail: newMode }))
  }

  const modes = [
    { value: 'paper', label: t('paperOnly'), emoji: '📄' },
    { value: 'gradient', label: t('gradientOnly'), emoji: '🌈' },
    { value: 'both', label: t('bothCombined'), emoji: '✨' },
    { value: 'none', label: t('noneClean'), emoji: '⚪' }
  ]

  return (
    <div className="flex items-center gap-2">
      <Paintbrush className="w-4 h-4" />
      <span className="text-sm">{t('backgroundLabel')}</span>
      <div className="flex gap-1">
        {modes.map((modeOption) => (
          <button
            key={modeOption.value}
            onClick={() => changeMode(modeOption.value as BackgroundMode)}
            className={`w-8 h-8 rounded text-xs hover:bg-gray-100 transition-colors ${
              mode === modeOption.value ? 'bg-blue-100' : ''
            }`}
            title={modeOption.label}
          >
            {modeOption.emoji}
          </button>
        ))}
      </div>
    </div>
  )
}

export function useBackgroundMode() {
  const [mode, setMode] = useState<BackgroundMode>('paper')

  useEffect(() => {
    const saved = localStorage.getItem('imki-bg-mode') as BackgroundMode
    if (saved) setMode(saved)

    const handler = (e: CustomEvent<BackgroundMode>) => setMode(e.detail)
    window.addEventListener('background-mode-change', handler as EventListener)
    return () => window.removeEventListener('background-mode-change', handler as EventListener)
  }, [])

  return mode
}
